import { ArrowDownIcon, ArrowUpIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import { useMemo } from 'react'

import { useGetTransactionActiveWorkTrendQuery } from '../../../app/apiSliceProcessApi'
import { InlineLoader } from '../../../common/components'
import { useEnhancedNavigate } from '../../../common/hooks'
import { MainPathEnum, SubPathEnum } from '../../../common/types/routing'
import { SubSearchParamEnum } from '../../../common/types/searchParams'
import { formatNumber } from '../../../common/utils/numberFormatting'
import { type ProcessAnalysisConfig, TrendTimeGranularityEnum } from '../types'
import TrackedProcessCardLead from './TrackedProcessCardLead'
import { yearAndWeekToKey } from './utils'

interface Props {
  process: ProcessAnalysisConfig
  totalAppUsageTrendMapper: { [key: string]: number }
}

const SHARE_LABEL = 'Share of weekly work time'
const SHARE_TITLE =
  'Share of this process in all tracked application time for the latest full week vs the week before'

const TrackedProcessStat = ({ process, totalAppUsageTrendMapper }: Props) => {
  const { enhancedNavigate: navigate } = useEnhancedNavigate()
  const { data, isLoading } = useGetTransactionActiveWorkTrendQuery({
    processId: process.id,
    filters: {},
    trend_granularity: TrendTimeGranularityEnum.WEEK,
  })

  const [currentValue, prevWeekValue] = useMemo(() => {
    if (!data || data.length === 0) return [0, 0]

    const getWeeklyData = (index: number) => {
      if (!data || index < 0) return [0, 0]

      const { year, isoWeekNumber, total_duration_sec: totalProcessTime } = data[index]
      const totalAppUsageTime = totalAppUsageTrendMapper[yearAndWeekToKey(year, isoWeekNumber)] ?? 1
      return [totalProcessTime, totalAppUsageTime]
    }

    let curValue, prevValue
    if (data.length < 3) {
      const [totalProcessTime, totalAppUsageTime] = getWeeklyData(0)
      curValue = (totalProcessTime / totalAppUsageTime) * 100
      prevValue = 0
    } else {
      const [totalProcessTime, totalAppUsageTime] = getWeeklyData(data.length - 2)
      const [totalProcessTimePrev, totalAppUsageTimePrev] = getWeeklyData(data.length - 3)
      curValue = (totalProcessTime / totalAppUsageTime) * 100
      prevValue = (totalProcessTimePrev / totalAppUsageTimePrev) * 100
    }

    return [curValue, prevValue]
  }, [data, totalAppUsageTrendMapper])

  const value = currentValue ?? 0
  const prevValue = prevWeekValue ?? 0
  const processLink = `${MainPathEnum.PROCESSES}/${process.id}/${SubPathEnum.OVERVIEW}`
  const activeFilterId = process.active_filter_id
  const delta = value - prevValue
  const isDeltaPositive = delta >= 0
  const showWeekOverWeek = Boolean(data && data.length >= 3)

  const openProcess = () => {
    navigate(`../${processLink}`, {
      search: `?${SubSearchParamEnum.PROCESS_FILTER}=${activeFilterId}`,
    })
  }

  return (
    <button
      type='button'
      onClick={openProcess}
      className='group flex w-full flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-slate-200/90 bg-white px-4 py-3 text-left shadow-sm ring-offset-2 transition hover:border-primary-300 hover:bg-primary-50/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
    >
      <TrackedProcessCardLead process={process} />

      <div className='flex shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3'>
        <div className='border-l border-slate-200 pl-3 sm:pl-4' title={SHARE_TITLE}>
          {isLoading ? (
            <InlineLoader />
          ) : (
            <div className='text-right sm:min-w-[9.5rem]'>
              <p
                className='text-left text-[10px] font-medium leading-snug text-slate-500 sm:text-right'
                title={SHARE_TITLE}
              >
                {SHARE_LABEL}
              </p>
              <div className='mt-1 space-y-0.5'>
                <p className='text-2xl font-bold tabular-nums leading-none text-slate-900'>
                  {formatNumber(Math.min(value, 100), 10)}%
                </p>
                {!showWeekOverWeek ? null : (
                  <div
                    className={clsx(
                      'flex flex-wrap items-center justify-end gap-x-1.5 gap-y-0.5 text-xs font-semibold tabular-nums',
                      delta === 0
                        ? 'text-slate-600'
                        : isDeltaPositive
                          ? 'text-green-700'
                          : 'text-red-700',
                    )}
                  >
                    <span className='inline-flex items-center gap-0.5'>
                      {delta !== 0 &&
                        (isDeltaPositive ? (
                          <ArrowUpIcon className='size-3 shrink-0' />
                        ) : (
                          <ArrowDownIcon className='size-3 shrink-0' />
                        ))}
                      {delta === 0 ? '' : isDeltaPositive ? '+' : '−'}
                      {formatNumber(Math.min(Math.abs(delta), 100), 10)}%
                    </span>
                    <span className='shrink-0 leading-none text-slate-500'>vs prior week</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <ChevronRightIcon className='size-5 shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-primary-600' />
      </div>
    </button>
  )
}

export default TrackedProcessStat

